import React from 'react';
import { useTranslation } from 'react-i18next';

const InfoRow = ({ label, children, muted }) => (
  <div className="flex items-baseline justify-between py-[5px]">
    <span className="text-[12px] text-[#8993a4] select-none">{label}</span>
    <span className={`text-[13px] font-medium ${muted ? 'text-[#8993a4]' : 'text-[#2d3643]'}`}>
      {children}
    </span>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-[#8993a4] uppercase tracking-[0.06em]">
    {children}
  </p>
);

const STATUS_STYLES = {
  pending: 'bg-[#fef9c3] text-[#854d0e]',
  approved: 'bg-[#dcfce7] text-[#166534]',
  rejected: 'bg-[#fee2e2] text-[#991b1b]',
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || 'bg-[#f0f2f4] text-[#5f6f86]';
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${style}`}>
      {status || '-'}
    </span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

const RefundStateCard = ({ state }) => {
  const { t } = useTranslation();
  const tl = (key) => t(`admin.auditLogs.refundCard.${key}`);

  if (!state) {
    return (
      <div className="bg-white border border-[#e5e7eb] rounded-xl p-5">
        <span className="text-[13px] text-[#8993a4]">-</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
      <div className="px-5 py-3">
        {/* Status */}
        <div className="flex items-baseline justify-between py-[5px]">
          <span className="text-[12px] text-[#8993a4] select-none">{tl('status')}</span>
          <StatusBadge status={state.status} />
        </div>

        {/* User Info */}
        {state.userName && <InfoRow label={tl('user')}>{state.userName}</InfoRow>}
        {state.userEmail && <InfoRow label={tl('email')} muted>{state.userEmail}</InfoRow>}

        {/* IDs */}
        {state.refundRequestId && (
          <InfoRow label={tl('refundRequestId')} muted>{state.refundRequestId}</InfoRow>
        )}
        {state.billingSubscriptionId && (
          <InfoRow label={tl('billingSubscriptionId')} muted>{state.billingSubscriptionId}</InfoRow>
        )}

        {/* Financial details */}
        {(state.amount !== undefined || state.currency) && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <SectionLabel>{tl('payment')}</SectionLabel>
            {state.amount !== undefined && (
              <InfoRow label={tl('amount')}>
                {state.currency
                  ? `${state.currency.toUpperCase()} ${state.amount}`
                  : state.amount}
              </InfoRow>
            )}
            {state.provider && <InfoRow label={tl('provider')}>{state.provider}</InfoRow>}
            {state.subscriptionInterval && (
              <InfoRow label={tl('interval')}>{state.subscriptionInterval}</InfoRow>
            )}
            {state.subscriptionStatus && (
              <InfoRow label={tl('subscriptionStatus')}>{state.subscriptionStatus}</InfoRow>
            )}
            {state.providerPaymentId && (
              <InfoRow label={tl('providerPaymentId')} muted>{state.providerPaymentId}</InfoRow>
            )}
          </>
        )}

        {/* User reason */}
        {state.reason && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <SectionLabel>{tl('reason')}</SectionLabel>
            <p className="text-[12px] text-[#2d3643] mt-1 leading-relaxed">{state.reason}</p>
          </>
        )}

        {/* Approval details */}
        {state.approvalReason && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <SectionLabel>{tl('approval')}</SectionLabel>
            <p className="text-[12px] text-[#2d3643] mt-1 leading-relaxed">{state.approvalReason}</p>
            {state.providerRefundId && (
              <InfoRow label={tl('providerRefundId')} muted>{state.providerRefundId}</InfoRow>
            )}
            {state.providerSubscriptionId && (
              <InfoRow label={tl('providerSubscriptionId')} muted>{state.providerSubscriptionId}</InfoRow>
            )}
          </>
        )}

        {/* Rejection details */}
        {state.rejectionReason && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <SectionLabel>{tl('rejection')}</SectionLabel>
            <p className="text-[12px] text-[#2d3643] mt-1 leading-relaxed">{state.rejectionReason}</p>
          </>
        )}

        {/* Processed at */}
        {state.processedAt && (
          <>
            <div className="h-px bg-[#f0f2f4] my-2" />
            <InfoRow label={tl('processedAt')} muted>{formatDate(state.processedAt)}</InfoRow>
          </>
        )}
      </div>
    </div>
  );
};

export default RefundStateCard;
